import { useCallback, useEffect, useState } from 'react';

// Components
import CustomDataTable from '../../components/DataTable';
// import { ResumeButton } from '../../components/ActionButtons';
import UserDetails from '../../components/popup/UserDetails';

// Constants
import {
  APPLIED_JOB_STATUS,
  CANDIDATE_LOCATIONS,
  CANDIDATE_SOURCE_TYPE,
  CANDIDATE_SOURCE_REFERENCE_CODE,
  DEBOUNCE_TIMEOUT,
  INITIAL_PAGE_NUMBER,
  INITIAL_SIZE,
  JobModes,
  TABLE_NAME
} from '../../constants/common';
import { JOBS_APPLICATION_COLUMNS } from '../../constants/tableColumns';

// Utility Functions
import { getJobApplicationsList } from '../../redux/actions/jobApplication';
import { updateMatchingPercentageApi } from '../../redux/apis/jobApplication';
import { candidateDetailsAction } from '../../redux/actions/userDetails';
import {
  formatExperience,
  normalizeObject,
  openResumeLink,
  renderHrAvatar,
  wordCapitalize
} from '../../utils/common';
import { debounce } from '../../utils/debounce';

// Utility Packages
import moment from 'moment';
import { Badge } from 'primereact/badge';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import {
  ActionButtonForAppliedJob,
  CustomeTooltip,
  ResumeButton
} from '../../components/ActionButtons';
import ChangeAppliedJobStatus from '../../components/popup/ChangeAppliedJobStatus';
import ViewJobDetails from '../../components/popup/ViewJobDetails';
import { getInterviewTechnology } from '../../redux/actions/hr';
import { getJobById } from '../../redux/actions/jobs';
import { MatchProfileEvaluation } from '../../components/popup/MatchProfileEvaluation';
import CopyToClipboard from '../../components/CopyToClipboard';
import { Button } from 'primereact/button';

const jobApplicationsTableStates = {
  searchValue: '',
  pageNumber: INITIAL_PAGE_NUMBER,
  size: INITIAL_SIZE,
  fromHr: true,
  sortBy: 'DateCreated',
  orderBy: 'desc',
  technology: '',
  minExp: null,
  maxExp: null,
  location: null,
  status: null
};

const profilePathBase = `${process.env.REACT_APP_API_ENDPOINT}/public/uploads/profile`;

const AppliedJobs = () => {
  const dispatch = useDispatch();
  const technologyListData = useSelector((state) => state.hrs.interviewTechnologyType);
  const jobApplications = useSelector((state) => state.jobApplications);
  const { userDetails } = useSelector((state) => state);
  const { jobDetails } = useSelector((state) => state.jobs);

  // State
  const [tableStates, setTableStates] = useState(jobApplicationsTableStates);
  const [jobApplicationsData, setJobApplicationsData] = useState([]);
  const [isHoveredImage, setIsHoveredImage] = useState({});
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  // const [expandedRows, setExpandedRows] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showJobPopup, setShowJobPopup] = useState(false);
  const [candidateDetails, setCandidateDetails] = useState([]);
  const [jobDetailsData, setJobDetailsData] = useState([]);
  const [candidateId, setCandidateId] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [statusPopup, setStatusPopup] = useState(false);
  const [statusData, setStatusData] = useState(null);
  const [matchEvaluationData, setMatchEvalucationData] = useState(null);
  const [matchEvaluationDataPopup, setMatchEvalucationDataPopup] = useState(false);
  const [loadingMatches, setLoadingMatches] = useState({});

  const handleFindMatch = async (candidateId) => {
    setLoadingMatches((prev) => ({ ...prev, [candidateId]: true }));
    try {
      const response = await updateMatchingPercentageApi(candidateId);
      if (response?.status === 200) {
        toast.success(response?.data?.message || 'Matching percentage updated successfully', {
          position: toast.POSITION.BOTTOM_RIGHT
        });
        dispatch(getJobApplicationsList(tableStates));
      } else {
        toast.error(response?.data?.message || 'Failed to update matching percentage', {
          position: toast.POSITION.BOTTOM_RIGHT
        });
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to update matching percentage', {
        position: toast.POSITION.BOTTOM_RIGHT
      });
    } finally {
      setLoadingMatches((prev) => ({ ...prev, [candidateId]: false }));
    }
  };

  const CANDIDATE_LOCATIONS_FILTER = CANDIDATE_LOCATIONS.filter(
    (location) => location.code !== 'other'
  );

  const APPLIED_JOB_STATUS_FILTER = APPLIED_JOB_STATUS.map((status) => ({
    name: status.name,
    code: status.code
  }));

  // Experience Year Filter
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    dispatch(getInterviewTechnology());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getJobApplicationsList(tableStates));
  }, [
    tableStates,
    jobApplications?.isDeleted,
    jobApplications?.isUpdated,
    jobApplications?.isAdded
  ]);

  const onTogglePopup = () => setShowPopup((prevState) => !prevState);
  const onToggleJobPopup = () => setShowJobPopup((prevState) => !prevState);

  const modifyUserDetails = useCallback(() => {
    if (userDetails?.ID) {
      // Display -- if the candidate mobile number is not provided.
      let mobileValue = '--';
      if (userDetails?.Mobile) {
        mobileValue = userDetails?.Mobile;
      }
      if (mobileValue && mobileValue === 'null') {
        mobileValue = '--';
      }
      const candidateDetailsData = [
        {
          title: 'Name',
          value: `${wordCapitalize(userDetails?.FirstName) || ''} 
              ${wordCapitalize(userDetails?.LastName) || ''}`
        },
        {
          title: 'Gender',
          value: userDetails?.Gender == 1 ? 'Male' : userDetails?.Gender == 2 ? 'Female' : '--'
        },
        {
          title: 'Experience',
          value: formatExperience(userDetails?.YearsOfExperience, userDetails?.MonthsOfExperience)
        },
        {
          title: 'Email Address',
          value: (
            <div className="center">
              <span>{userDetails?.Email || '--'}</span>
              {userDetails?.Email && <CopyToClipboard text={userDetails?.Email} />}
            </div>
          )
        },
        {
          title: 'Mobile',
          value: (
            <div className="center">
              <span>{mobileValue || '--'}</span>
              {userDetails?.Mobile && <CopyToClipboard text={mobileValue} />}
            </div>
          )
        },
        {
          title: 'Second Mobile',
          value: (
            <div className="center">
              <span>{userDetails?.SecondaryMobile || '--'}</span>
              {userDetails?.SecondaryMobile && (
                <CopyToClipboard text={userDetails?.SecondaryMobile} />
              )}
            </div>
          )
        },
        {
          title: 'Current Company',
          value: userDetails?.CurrentCompany || ''
        },
        {
          title: 'Technology',
          value:
            userDetails.CandidateTechnology && userDetails.CandidateTechnology.length > 0
              ? userDetails.CandidateTechnology?.map((tech) => tech.Technology).join(', ')
              : ''
        },
        {
          title: 'Current CTC',
          value: userDetails?.CurrentCTC ? `${userDetails?.CurrentCTC} LPA` : ''
        },
        {
          title: 'Candidate Location',
          value: userDetails?.Address || ''
        },
        {
          title: 'Candidate Source',
          value: CANDIDATE_SOURCE_TYPE.find((type) => type.code === userDetails?.CandidateSource)
            ?.name
        }
      ];
      // Only add the candidate reference name if candidate provided source is reference.
      if (userDetails?.CandidateSource === CANDIDATE_SOURCE_REFERENCE_CODE) {
        candidateDetailsData.push({
          title: 'Employee Ref Name',
          value: userDetails?.EmployeeReferenceName || ''
        });
      }
      candidateDetailsData.push(
        {
          title: 'Created By',
          value: renderHrAvatar(
            userDetails?.ReportsToUser?.ID,
            userDetails?.ReportsToUser?.UserProfile,
            userDetails?.ReportsToUser?.FirstName,
            userDetails?.ReportsToUser?.LastName,
            true
          )
        },
        {
          title: 'Last Updated Time',
          value: userDetails?.DateModified ? moment(userDetails?.DateModified).format('lll') : null
        }
      );
      setCandidateDetails(candidateDetailsData);
    }
  }, [userDetails]);

  const modifyJobDetails = useCallback(() => {
    if (jobDetails?.ID) {
      const jobDetailsData = [
        {
          title: 'Job Title',
          value: `${wordCapitalize(jobDetails?.JobTitle) || '--'}`
        },
        {
          title: 'Position',
          value: jobDetails?.Position || '--'
        },
        {
          title: 'Technology',
          value:
            jobDetails?.technologies && jobDetails.technologies.length > 0
              ? jobDetails.technologies.map((tech) => tech.Technology || tech.name).join(', ')
              : '--'
        },
        {
          title: 'Experience',
          value: jobDetails?.Experience ? `${jobDetails?.Experience} Y` : '--'
        },
        {
          title: 'Skills',
          value:
            jobDetails.Skills && jobDetails.Skills.length > 0 ? jobDetails.Skills?.join(', ') : ''
        },
        {
          title: 'Job Mode',
          value: JobModes.find((mode) => mode.code === jobDetails?.JobMode)?.name || '--'
        },
        {
          title: 'Description',
          value: `${jobDetails.JobDescription}`
        }
      ];
      setJobDetailsData(jobDetailsData);
      setMatchEvalucationData((prev) => ({ ...prev, Description: jobDetails?.JobDescription }));
    }
  }, [jobDetails]);

  useEffect(() => {
    modifyUserDetails();
  }, [userDetails, modifyUserDetails]);

  useEffect(() => {
    modifyJobDetails();
  }, [jobDetails, modifyJobDetails]);

  const handlePagination = (pageNumber, size) =>
    setTableStates((prevState) => {
      return {
        ...prevState,
        pageNumber,
        size
      };
    });

  const handleSearch = (searchValue) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        searchValue
      };
    });
  };

  const handleMouseMove = (event) => {
    setMousePosition({ x: event.clientX, y: event.clientY });
  };

  const handleView = (ID) => {
    if (ID) {
      onTogglePopup();
      setCandidateId(ID);
    }
  };

  const handleViewJobDetails = (ID) => {
    if (ID) {
      onToggleJobPopup();
      setJobId(ID);
    }
  };

  useEffect(() => {
    const modifiedJobs = jobApplications?.jobApplications?.map((candidate, index) => {
      const appliedJobStatus = APPLIED_JOB_STATUS.find(
        (status) => status.code === candidate?.AppliedJobs?.Status
      );
      return {
        ...candidate,
        ...candidate?.JobDetails,
        CandidateName: (
          <div className="flex" style={{ position: 'relative' }}>
            <span
              className="table-view-popup-link"
              onClick={() => handleView(candidate?.ID)}
              title={`Mobile : ${candidate.Mobile || '--'}`}>
              {`${wordCapitalize(candidate.FirstName)} ${wordCapitalize(candidate.LastName)}`}
            </span>
            {candidate?.UserProfile && candidate?.ID && (
              <div
                onMouseEnter={() => {
                  if (candidate?.UserProfile && candidate?.ID) {
                    let hoveredImage = { ...isHoveredImage, [candidate?.ID]: true };
                    setIsHoveredImage(hoveredImage);
                  }
                }}
                onMouseLeave={() => {
                  let hoveredImage = { ...isHoveredImage, [candidate?.ID]: false };
                  setIsHoveredImage(hoveredImage);
                }}
                onMouseMove={handleMouseMove}>
                <i
                  onClick={() =>
                    openResumeLink(`${profilePathBase}/${candidate?.ID}/${candidate?.UserProfile}`)
                  }
                  className="pi pi-fw pi-eye"
                />
                {isHoveredImage[candidate?.ID] && (
                  <div
                    className="image-preview"
                    key={index}
                    style={{
                      top: `${mousePosition.y + 10}px`,
                      left: `${mousePosition.x + 10}px`
                    }}>
                    <img
                      src={`${profilePathBase}/${candidate?.ID}/${candidate?.UserProfile}`}
                      alt="Preview"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        ),
        CandidateExperience: formatExperience(
          candidate?.YearsOfExperience,
          candidate?.MonthsOfExperience
        ),
        // JobExperience: candidate?.AppliedJobs?.TotalExperience
        //   ? `${candidate?.AppliedJobs?.TotalExperience} Y`
        //   : '--',
        JobTitle: (
          <div className="flex" style={{ position: 'relative' }}>
            <span
              className="table-view-popup-link"
              onClick={() => handleViewJobDetails(candidate?.AppliedJobs?.JobId)}>
              {candidate?.AppliedJobs?.JobDetails?.JobTitle || '--'}
            </span>
          </div>
        ),
        Qualification: candidate?.AppliedJobs?.HighestQualification?.Course || '--',
        CurrentCompany: candidate?.CurrentCompany || '--',
        MatchEvaluation: (() => {
          const fitScore = candidate?.AppliedJobs?.MatchEvaluation?.fitScore;
          const hasMatchScore =
            fitScore !== undefined && fitScore !== null && fitScore !== 0 && fitScore !== '0';

          if (!hasMatchScore) {
            return (
              <div
                className="flex justify-content-center align-items-center"
                style={{ whiteSpace: 'nowrap' }}>
                <Button
                  label="Find Match"
                  className="p-button-sm p-button-outlined"
                  style={{
                    fontSize: '11px',
                    padding: '4px 10px',
                    height: '26px',
                    whiteSpace: 'nowrap',
                    lineHeight: '1'
                  }}
                  onClick={() => handleFindMatch(candidate?.ID)}
                  loading={loadingMatches[candidate?.ID]}
                  disabled={loadingMatches[candidate?.ID]}
                />
              </div>
            );
          }

          return (
            <div
              className="flex justify-content-center align-items-center"
              style={{ position: 'relative' }}>
              <span
                className="table-view-popup-link"
                onClick={() => {
                  setJobId(candidate?.AppliedJobs?.JobId);
                  setMatchEvalucationData((prev) => ({
                    ...prev,
                    ['Match Percentage']: `${fitScore || 0}%`,
                    Gaps: `${candidate?.AppliedJobs?.MatchEvaluation?.gaps?.join(', ') || '--'}`,
                    Strengths: `${
                      candidate?.AppliedJobs?.MatchEvaluation?.strengths?.join(', ') || '--'
                    }`,
                    Summary: `${candidate?.AppliedJobs?.MatchEvaluation?.summary || '--'}`
                  }));
                  setMatchEvalucationDataPopup(true);
                }}>
                {`${fitScore || 0}%`}
              </span>
            </div>
          );
        })(),
        // candidate?.AppliedJobs?.MatchEvaluation !== null &&
        // candidate?.AppliedJobs?.MatchEvaluation !== undefined
        //   ? `${candidate?.AppliedJobs?.MatchEvaluation?.fitScore || 0}%`
        //   : '--',
        CurrentCTC: `${candidate?.CurrentCTC != null ? `${candidate?.CurrentCTC} LPA` : '--'}`,
        ExpectedCTC: `${
          candidate?.AppliedJobs?.ExpectedCTC != null
            ? `${candidate?.AppliedJobs?.ExpectedCTC} LPA`
            : '--'
        }`,
        Location: candidate?.Address || '--',
        Resume: (
          <div className="flex icon-center">
            {candidate?.AppliedJobs?.Resume?.FilePath && (
              <>
                <ResumeButton
                  onClick={() => {
                    openResumeLink(candidate?.AppliedJobs?.Resume?.FilePath);
                  }}
                />
              </>
            )}
          </div>
        ),
        NoticePeriod: candidate?.AppliedJobs?.NoticePeriod
          ? candidate?.AppliedJobs?.NoticePeriod !== '0'
            ? `${candidate?.AppliedJobs?.NoticePeriod} Months`
            : 'Immediate'
          : '--',
        Status: (
          <div className="flex justify-content-center align-items-center">
            <Badge
              className={`white-space-nowrap ${appliedJobStatus?.class}`}
              value={appliedJobStatus?.name || ''}
            />
            {candidate?.AppliedJobs?.Comment && (
              <>
                <i
                  className="custom-target-icon pi pi-info-circle pl-1"
                  data-tip={candidate?.AppliedJobs?.Comment}
                  style={{ cursor: 'pointer' }}
                />
                <CustomeTooltip />
              </>
            )}
          </div>
        ),
        DateCreated: candidate?.AppliedJobs?.DateCreated
          ? moment(candidate?.AppliedJobs?.DateCreated).format('DD-MM-YYYY')
          : '',
        action: candidate?.AppliedJobs?.Status !==
          APPLIED_JOB_STATUS.find((obj) => obj.id === 'selected').code && (
          <div className="flex align-items-center">
            <ActionButtonForAppliedJob
              code={candidate?.AppliedJobs?.Status}
              onClick={(value) => handleStatus(candidate.AppliedJobs.ID, value)}
            />
          </div>
        )
      };
    });
    setJobApplicationsData(modifiedJobs);
  }, [jobApplications, isHoveredImage]);

  useEffect(() => {
    candidateId && dispatch(candidateDetailsAction(candidateId));
  }, [dispatch, candidateId, userDetails?.isCandidateUpdated]);

  useEffect(() => {
    jobId && dispatch(getJobById(jobId));
  }, [dispatch, jobId]);
  // const rowExpansionTemplate = (data) => {
  //   return (
  //     <div className="applied-job py-3">
  //       <DataTable value={data.AppliedJobs}>
  //         <Column header="Job Title" field="JobDetails.JobTitle" className="name-col" />
  //         <Column header="Total Ex." field="TotalExperience" />
  //         <Column header="Current Company" field="CurrentCompany" />
  //         <Column header="Current CTC" field="CurrentCTC" />
  //         <Column header="Expected CTC" field="ExpectedCTC" />
  //         <Column header="Notice Period" field="NoticePeriod" />
  //         <Column header="Highest Qualification" field="HighestQualification.Course" />
  //         <Column header="Resume" field="Resume.FilePath" />
  //       </DataTable>
  //     </div>
  //   );
  // };

  const handleTechnologyFilter = (value) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        technology: value || ''
      };
    });
  };

  const handleYearExperienceFilter = (minExp, maxExp) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        minExp,
        maxExp
      };
    });
    handleClose();
  };

  const handleSort = (sortBy) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        sortBy,
        orderBy: prevState.orderBy === 'asc' ? 'desc' : 'asc'
      };
    });
  };

  const handleLocationFilter = (value) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        location: value
      };
    });
  };
  const handleStatusFilter = (value) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        status: value
      };
    });
  };
  const handleStatus = (id, value) => {
    setStatusData({ id, value });
    setStatusPopup(true);
  };

  const handleStatusClose = () => {
    setStatusData(null);
    setStatusPopup(false);
  };

  return (
    <>
      <UserDetails
        header="Candidate Details"
        data={candidateDetails}
        onHide={onTogglePopup}
        show={showPopup}
      />
      <ViewJobDetails
        header="Job Details"
        data={jobDetailsData}
        onHide={onToggleJobPopup}
        show={showJobPopup}
      />
      <ChangeAppliedJobStatus
        statusData={statusData}
        open={statusPopup}
        onHide={handleStatusClose}
        tableStates={tableStates}
      />

      <MatchProfileEvaluation
        show={matchEvaluationDataPopup}
        onHide={() => setMatchEvalucationDataPopup(false)}
        matchEvaluationData={matchEvaluationData}
      />

      <CustomDataTable
        // expandedRows={expandedRows}
        // onRowToggle={(e) => setExpandedRows(e.data)}
        // rowExpansionTemplate={rowExpansionTemplate}
        name={TABLE_NAME.APPLIED_JOBS}
        searchPlaceHolder="Search by Title"
        totalRecords={jobApplications?.pagination?.totalRecords || 0}
        onPagination={handlePagination}
        pageNumber={tableStates.pageNumber}
        pageSize={tableStates.size}
        onSearch={debounce(handleSearch, DEBOUNCE_TIMEOUT)}
        onSort={handleSort}
        data={jobApplicationsData}
        columns={JOBS_APPLICATION_COLUMNS}
        experienceFilter
        open={open}
        label={
          tableStates.minExp && tableStates.maxExp
            ? `${tableStates.minExp} - ${tableStates.maxExp} Years`
            : 'Years of Exp'
        }
        onExperienceFilter={handleClick}
        minExp={tableStates.minExp}
        maxExp={tableStates.maxExp}
        handleYearExperienceFilter={handleYearExperienceFilter}
        handleClose={handleClose}
        anchorEl={anchorEl}
        isDropdownMultiSelect
        dropdownPlaceHolder="Select Technology"
        onStatusFilter={handleTechnologyFilter}
        dropdown={
          technologyListData?.length
            ? technologyListData.map((tech) => ({ name: tech.Technology, value: tech.ID }))
            : []
        }
        dropdownValue={tableStates.technology}
        secondDropdown={CANDIDATE_LOCATIONS_FILTER}
        secondDropdownValue={tableStates.location}
        secondDropdownPlaceHolder="Select Location"
        onSecondDropdownChange={(val) => handleLocationFilter(val)}
        thirdDropdown={APPLIED_JOB_STATUS_FILTER}
        thirdDropdownValue={tableStates.status}
        thirdDropdownPlaceHolder="Select Status"
        onThirdDropdownChange={(val) => handleStatusFilter(val)}
        clearFilter
        isClearFilterDisabled={
          JSON.stringify(normalizeObject(jobApplicationsTableStates)) ===
          JSON.stringify(normalizeObject(tableStates))
        }
        clearFilterOnClick={() => setTableStates(jobApplicationsTableStates)}
      />
    </>
  );
};

export default AppliedJobs;
